import { MarkupPreview } from './MarkupPreview';
import type { MarkupShape } from '@/types/markup.types';
import type { Photo } from '@/types/photo';
import logger from '@/utils/logger';

interface MarkupAttachmentPreviewProps {
  photo: Photo;
  shapes: MarkupShape[];
  onClick?: () => void;
  /** Feature 652: Callback when user clicks edit button */
  onEdit?: () => void;
  /** Feature 652: Callback when user clicks remove button */
  onRemove?: () => void;
  className?: string;
}

/**
 * 240x240px thumbnail preview of markup annotation for display in comment forms
 * and critique feed. Clicking the thumbnail opens a full-size preview modal.
 */
export function MarkupAttachmentPreview({
  photo,
  shapes,
  onClick,
  onEdit,
  onRemove,
  className = '',
}: MarkupAttachmentPreviewProps) {
  const handleClick = () => {
    if (onClick) {
      logger.info('MarkupAttachmentPreview', 'Thumbnail clicked, opening full preview');
      onClick();
    }
  };

  // Feature 652: Handle edit button click
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering onClick
    if (onEdit) {
      logger.info('MarkupAttachmentPreview', 'Edit button clicked');
      onEdit();
    }
  };

  // Feature 652: Handle remove button click
  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering onClick
    if (onRemove) {
      logger.info('MarkupAttachmentPreview', 'Remove button clicked');
      onRemove();
    }
  };

  return (
    <div
      className={`group relative inline-block border-2 border-border rounded overflow-hidden w-60 h-60 ${
        onClick ? 'cursor-pointer hover:border-primary' : ''
      } ${className}`}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <MarkupPreview
        photo={photo}
        shapes={shapes}
        disableModal={true}
      />

      {/* Feature 652: Hover-reveal action buttons */}
      {(onEdit || onRemove) && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex gap-1">
          {onEdit && (
            <button
              type="button"
              onClick={handleEdit}
              className="p-1.5 bg-surface-1/90 hover:bg-surface-2 focus:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded border border-border shadow-sm transition-colors"
              aria-label="Edit markup annotation"
            >
              <svg className="w-4 h-4 text-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={handleRemove}
              className="p-1.5 bg-surface-1/90 hover:bg-danger/20 hover:text-danger focus:bg-danger/20 focus:text-danger focus:outline-none focus:ring-2 focus:ring-danger focus:ring-offset-1 rounded border border-border shadow-sm transition-colors"
              aria-label="Remove markup annotation"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
